import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Minus, Plus, X, Lock } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/store/StoreContext";
import { api, img, money, formatApiError } from "@/lib/api";

export default function CartDrawer() {
  const { cart, cartTotal, cartOpen, setCartOpen, updateQty, removeFromCart, user } = useStore();
  const [checkingOut, setCheckingOut] = useState(false);
  const [email, setEmail] = useState("");

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: cart.map((i) => ({ slug: i.slug, size: i.size, qty: i.qty, piece_sizes: i.piece_sizes || undefined })),
        origin_url: window.location.origin,
        email: email || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            className="fixed top-0 right-0 bottom-0 z-[80] w-full sm:w-[420px] bg-noir-panel border-l border-white/10 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
              <h2 className="font-display font-bold uppercase tracking-[0.2em] text-sm">Your Bag</h2>
              <button onClick={() => setCartOpen(false)} data-testid="cart-drawer-close" aria-label="Close cart">
                <X size={20} strokeWidth={1.5} className="text-cream/70 hover:text-gold transition-colors" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <p className="font-display uppercase tracking-[0.15em] text-cream/60 mb-2">The bag is empty</p>
                  <p className="text-sm text-cream/40 mb-8">Silence is heavy. So is 450 GSM.</p>
                  <Link to="/shop" onClick={() => setCartOpen(false)} className="font-mono2 text-[10px] tracking-[0.25em] uppercase border border-gold/60 text-gold px-6 py-3 hover:bg-gold hover:text-noir-bg transition-colors duration-300" data-testid="cart-empty-shop-link">
                    Shop the Drop
                  </Link>
                </div>
              ) : (
                <ul className="space-y-6">
                  {cart.map((item) => (
                    <motion.li key={item.key} layout className="flex gap-4" data-testid={`cart-item-${item.key}`}>
                      <Link to={`/product/${item.slug}`} onClick={() => setCartOpen(false)} className="w-20 aspect-[3/4] bg-noir-card overflow-hidden shrink-0">
                        <img src={img(item.image)} alt={item.name} className="w-full h-full object-cover" />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <Link to={`/product/${item.slug}`} onClick={() => setCartOpen(false)} className="font-display text-sm uppercase tracking-wide truncate hover:text-gold transition-colors">{item.name}</Link>
                          <button onClick={() => removeFromCart(item.key)} data-testid={`cart-remove-${item.key}`} aria-label="Remove item">
                            <X size={14} className="text-cream/40 hover:text-gold transition-colors" />
                          </button>
                        </div>
                        <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-cream/40 mt-1">
                          {item.piece_sizes
                            ? Object.entries(item.piece_sizes).map(([k, sz]) => `${item.piece_labels?.[k] || k}: ${sz}`).join(" · ")
                            : `Size ${item.size}`}
                        </p>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-white/15">
                            <button onClick={() => updateQty(item.key, -1)} className="px-2 py-1 text-cream/60 hover:text-gold" data-testid={`cart-qty-minus-${item.key}`}><Minus size={12} /></button>
                            <span className="px-2 text-xs font-mono2" data-testid={`cart-qty-${item.key}`}>{item.qty}</span>
                            <button onClick={() => updateQty(item.key, 1)} className="px-2 py-1 text-cream/60 hover:text-gold" data-testid={`cart-qty-plus-${item.key}`}><Plus size={12} /></button>
                          </div>
                          <span className="font-mono2 text-sm text-gold">{money(item.price * item.qty)}</span>
                        </div>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              )}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-white/10 px-6 py-6">
                <div className="flex justify-between mb-5">
                  <span className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-cream/50">Subtotal</span>
                  <span className="font-mono2 text-gold" data-testid="cart-subtotal">{money(cartTotal)}</span>
                </div>
                {!user && (
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email — for your receipt"
                    className="w-full bg-noir-card border border-white/10 px-4 py-3 text-sm mb-4 focus:border-gold outline-none transition-colors placeholder:text-cream/30"
                    data-testid="cart-email-input"
                  />
                )}
                <button
                  onClick={checkout}
                  disabled={checkingOut}
                  className="w-full bg-gold text-noir-bg font-mono2 text-[11px] tracking-[0.25em] uppercase py-4 hover:bg-cream transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="cart-drawer-checkout-button"
                >
                  <Lock size={13} /> {checkingOut ? "Opening Secure Checkout…" : "Checkout"}
                </button>
                <p className="mt-4 text-center text-xs text-cream/40">
                  Secure checkout by Stripe. Tax calculated at checkout. Limited pieces are final sale.
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
